import { LockClosedIcon } from '@heroicons/react/24/outline'
import { Storage } from 'aws-amplify'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import * as Yup from 'yup'

import {
  UPDATE_ARCHIVED,
  UPDATE_BASIC_SETTINGS,
  UPDATE_REDIRECT_URL
} from '../../constants/career-history'
import { getAllJobCategories } from '../../constants/job-categories'
import { DE, FR, NL, getLanguage } from '../../constants/languages'
import trackEvents from '../../constants/track-events'
import {
  useAivyContext,
  useNotificationContext,
  usePartnerContext,
  usePaymentContext,
  useUserContext
} from '../../context'
import { useSampleDataDialog } from '../../context/sample-data-context'
import { useFeatureGate } from '../../features/pricing/use-feature-gate'
import { classNames, notifyBugsnag } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { getAssessmentLanguageDescription } from '../../helpers/assessment-language-description'
import { localStorage } from '../../helpers/local-storage'
import {
  getLockedAssessmentLanguages,
  getSelectableAssessmentLanguages
} from '../../helpers/mail-template-language'
import { isSampleCareer } from '../../helpers/sample-data'
import { useCreateLog } from '../../hooks/use-create-log'
import { useMailTemplates } from '../../hooks/use-mail-templates'
import Divider from '../divider'
import ImageCropAssessment from '../image-crop-assessment'
import Input from '../input'
import EditMailTemplate from '../mail-template/edit-mail-template'
import MailTemplatePreviewCard from '../mail-template/mail-template-preview-card'
import LockBadge from '../pricing/lock-badge'
import LockedSection from '../pricing/locked-section'
import SelectMenu from '../select-menu'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import UploadPicture from '../upload-picture'
import CareerSettingsContainer from './career-settings-container'
import ExpiresAfterDaysSettings from './expires-after-days-settings'

const TRANSLATION_LOCATION = 'pages.career.'

const REDIRECT_FEATURE = 'REDIRECT_AFTER_ASSESSMENT'
const REDIRECT_SOURCE = 'career_redirect'
const LANGUAGE_SOURCE = 'career_language'

// Eigene Vorlagen liegen je Stelle in app_settings (gleiche Felder wie in
// use-mail-templates.js und im Backend inviteTalent). Sie werden beim
// Sprachwechsel nicht übersetzt, darauf weist die Beschreibung hin.
const CUSTOM_MAIL_TEMPLATE_KEYS = [
  'mail_template',
  'mail_template_reminder',
  'mail_template_reject'
]

const AssessmentSettings = ({ career, updateHandler }) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { system } = useAivyContext()
  const { activeSpace } = useUserContext()
  const { redirectTalentUrl, showPaywall } = usePaymentContext()
  // Neue Lock-UX nur in enforced, in legacy und preview bleibt die Paywall
  const redirectInUse = !!career.redirect_url
  const redirectGate = useFeatureGate(REDIRECT_FEATURE, {
    source: REDIRECT_SOURCE,
    inUse: redirectInUse
  })
  const { success, error, alert } = useNotificationContext()
  const { openSampleDataDialog } = useSampleDataDialog()

  const navigate = useNavigate()

  const {
    mailTemplateMacros,
    mailTemplateMacrosReminder,
    mailTemplateMacrosReject,
    mailTemplate,
    mailTemplateReminder,
    mailTemplateReject
  } = useMailTemplates({ career })

  const { createCareerHistoryLog } = useCreateLog()

  const [isArchiving, setIsArchiving] = useState(false)

  const [showEditMailTemplate, setShowEditMailTemplate] = useState(false)
  const [showEditMailTemplateReminder, setShowEditMailTemplateReminder] =
    useState(false)
  const [showEditMailTemplateReject, setShowEditMailTemplateReject] =
    useState(false)

  const [photoUrl, setPhotoUrl] = useState(career.logo || partner.logo)

  const savedLanguage = career.language || DE
  const hasCustomMailTemplate = CUSTOM_MAIL_TEMPLATE_KEYS.some(
    (key) => !!career.app_settings?.[key]
  )

  // fr und nl sind nur in enforced gesperrt. Eine Stelle auf fr oder nl
  // behält ihre Sprache, weg wechseln geht, nach dem Speichern zurück nicht.
  // Die Seite ist kein Modal, eine gesperrte Option öffnet deshalb den
  // Upgrade-Dialog des globalen Hosts.
  const frGate = useFeatureGate('LANGUAGE_FR', {
    source: LANGUAGE_SOURCE,
    inUse: savedLanguage === FR
  })
  const nlGate = useFeatureGate('LANGUAGE_NL', {
    source: LANGUAGE_SOURCE,
    inUse: savedLanguage === NL
  })
  const languageGates = { [FR]: frGate, [NL]: nlGate }
  const languageOptions = getSelectableAssessmentLanguages({
    system,
    currentLanguage: savedLanguage
  })
  const lockedLanguages = getLockedAssessmentLanguages({
    fr: frGate.locked,
    nl: nlGate.locked,
    currentLanguage: savedLanguage,
    options: languageOptions
  })
  const keptLanguageGate = languageGates[savedLanguage]?.locked
    ? languageGates[savedLanguage]
    : null

  const formikGeneralSettings = useFormik({
    enableReinitialize: true,
    validationSchema: Yup.object({
      title: Yup.string().required(
        t(TRANSLATION_LOCATION + 'validation_required')
      )
    }),
    initialValues: {
      title: career.title,
      category: career.category || '-1',
      language: savedLanguage
    },
    onSubmit: ({ title, category, language }) => {
      updateHandler({
        input: { id: career.id, title, category, language },
        onSuccess: () => {
          createCareerHistoryLog({
            career_id: career.id,
            context: UPDATE_BASIC_SETTINGS,
            input: { title, category, language }
          }).then(() => {
            success(t('career.flex.update_career_success'))
            trackEvent(trackEvents.UPDATE_CAREER_ASSESSMENT_SETTINGS)
            formikGeneralSettings.setSubmitting(false)
          })
        },
        onError: () => formikGeneralSettings.setSubmitting(false)
      })
    }
  })

  const formikRedirectUrl = useFormik({
    enableReinitialize: true,
    validationSchema: Yup.object({
      redirect_url: Yup.string().url(
        t(
          'components.settings.company_profile.input_career_site_url_validation'
        )
      )
    }),
    initialValues: {
      redirect_url: career.redirect_url || ''
    },
    onSubmit: ({ redirect_url }) => {
      updateHandler({
        input: { id: career.id, redirect_url },
        onSuccess: () => {
          createCareerHistoryLog({
            career_id: career.id,
            context: UPDATE_REDIRECT_URL,
            input: { redirect_url }
          }).then(() => {
            success(t('career.flex.update_career_success'))
            trackEvent(trackEvents.UPDATE_CAREER_ASSESSMENT_SETTINGS)
            formikRedirectUrl.setSubmitting(false)
          })
        },
        onError: () => formikRedirectUrl.setSubmitting(false)
      })
    }
  })

  const updateURL = ({ values }) => {
    if (values.redirect_url && !values.redirect_url.startsWith('http')) {
      formikRedirectUrl.setFieldValue(
        'redirect_url',
        ['https://', values.redirect_url].join('')
      )
    }
  }

  // Abschalten geht immer. In enforced ohne das Feature lässt sich eine
  // bestehende Weiterleitung entfernen, aber nicht ändern.
  const removeRedirectUrl = () => {
    formikRedirectUrl.setSubmitting(true)

    updateHandler({
      input: { id: career.id, redirect_url: null },
      onSuccess: () => {
        createCareerHistoryLog({
          career_id: career.id,
          context: UPDATE_REDIRECT_URL,
          input: { redirect_url: null }
        })
          .then(() => {
            success(t('career.flex.update_career_success'))
            trackEvent(trackEvents.UPDATE_CAREER_ASSESSMENT_SETTINGS)
          })
          .finally(() => formikRedirectUrl.setSubmitting(false))
      },
      onError: () => formikRedirectUrl.setSubmitting(false)
    })
  }

  // Legacy und enforced rendern dasselbe Feld. In enforced gilt
  // redirectTalentUrl === !redirectGate.locked, das Feld ist dort also genau
  // dann gesperrt, wenn das Paket die Weiterleitung nicht enthält.
  const redirectUrlInput = (
    <Input
      id={'redirect_url'}
      label={t('career.flex.redirecturl_title')}
      type='text'
      disabled={!redirectTalentUrl}
      placeholder={redirectTalentUrl ? 'https://www.aivy.app' : undefined}
      description={t(
        'career.assessmentsettings.redirect_url.input_description'
      )}
      onChange={formikRedirectUrl.handleChange}
      onBlur={(event) =>
        formikRedirectUrl.handleBlur(event) || updateURL(formikRedirectUrl)
      }
      touched={formikRedirectUrl.touched.redirect_url}
      value={formikRedirectUrl.values.redirect_url}
      error={formikRedirectUrl.errors.redirect_url}
    />
  )

  const uploadPhoto = async ({ image }) => {
    if (!image) {
      error(t('components.settings.company_profile.update_partner_logo_error'))
      return
    }

    formikGeneralSettings.setSubmitting(true)

    try {
      const extension = image.name.split('.').pop()

      if (
        !extension ||
        !['jpg', 'jpeg', 'png', 'webp'].includes(extension.toLowerCase())
      ) {
        throw new Error('Invalid file extension')
      }

      const result = await Storage.put(
        `career-photos/${career.id}.${extension}`,
        image,
        {
          contentType: image.type,
          level: 'public'
        }
      )

      if (!result || !result.key) {
        throw new Error('Storage upload failed')
      }

      const updatedPhotoUrl = (await Storage.get(result.key)).split('?')[0]

      updateHandler({
        input: {
          id: career.id,
          logo: updatedPhotoUrl
        },
        onSuccess: () => {
          trackEvent(trackEvents.UPDATE_CAREER_ASSESSMENT_SETTINGS_PHOTO_URL)
          setPhotoUrl(updatedPhotoUrl)
          success(t('career.flex.update_career_success'))
        },
        onError: () => formikGeneralSettings.setSubmitting(false)
      })
    } catch (err) {
      console.error('Photo upload error:', err)

      let errorMessage = t(
        'components.settings.company_profile.update_partner_logo_error'
      )

      if (err.message?.includes('Network')) {
        errorMessage = t(
          'components.settings.company_profile.update_partner_logo_network_error'
        )
      } else if (err.message?.includes('size')) {
        errorMessage = t(
          'components.settings.company_profile.update_partner_logo_size_error'
        )
      }

      error(errorMessage)
      notifyBugsnag(err, { context: 'uploadPhoto', careerId: career.id })
    } finally {
      formikGeneralSettings.setSubmitting(false)
    }
  }

  const archiveCareer = () => {
    const { delete: can_delete } = activeSpace.authorization_role.crud

    if (!can_delete) {
      error(t('not_authorized_error_message'))
      return
    }

    // Beispielstellen werden nicht archiviert, sondern samt Beispieltalenten
    // entfernt. Der Dialog zeigt die Vorschau für diese Stelle. Danach leitet
    // useRemoveSampleData auf /careers weiter und leert den Filter von
    // /talents. Hat die Stelle echte Talente, bleibt sie als normale Stelle.
    if (isSampleCareer(career)) {
      openSampleDataDialog({
        mode: 'remove',
        scope: { career_ids: [career.id] },
        source: 'career_delete'
      })
      return
    }

    if (career.is_available_in_attract) {
      alert(
        t(TRANSLATION_LOCATION + 'modal_attract_active_title'),
        [t(TRANSLATION_LOCATION + 'modal_attract_active_text')],
        [
          {
            onPress: () => navigate(`/career/${career.id}#attract`),
            text: t(TRANSLATION_LOCATION + 'modal_attract_active_action')
          },
          {
            text: t(TRANSLATION_LOCATION + 'modal_cancel_action'),
            style: 'cancel'
          }
        ]
      )
      return
    }

    alert(
      t(TRANSLATION_LOCATION + 'modal_archive_title'),
      [t(TRANSLATION_LOCATION + 'modal_archive_text')],
      [
        {
          onPress: () => {
            setIsArchiving(true)

            updateHandler({
              input: { id: career.id, archived: true },
              onSuccess: () => {
                createCareerHistoryLog({
                  career_id: career.id,
                  context: UPDATE_ARCHIVED,
                  input: { archived: true }
                }).then(() => {
                  trackEvent(trackEvents.ARCHIVE_CAREER)
                  success(t(TRANSLATION_LOCATION + 'archive_success'))
                  setIsArchiving(false)

                  // make sure this career is not selected inside filter bar /talents
                  localStorage.setItem('/talents', '')

                  navigate('/careers')
                })
              }
            })
          },
          text: t(TRANSLATION_LOCATION + 'modal_archive_confirm_action'),
          style: 'destructive'
        },
        {
          text: t(TRANSLATION_LOCATION + 'modal_cancel_action'),
          style: 'cancel'
        }
      ]
    )
  }

  return (
    <div className='mb-12 mt-4'>
      <div className='my-6 md:flex md:items-center'>
        <div className='md:flex-auto'>
          <h1 className='text-xl font-semibold text-gray-900'>
            {t('career.assessmentsettings.title')}
          </h1>
          <p className='mt-2 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
            {t('career.assessmentsettings.description', {
              career_title: career.title
            })}
          </p>
        </div>
      </div>
      <Divider />
      <CareerSettingsContainer
        title={t('career.assessmentsettings.basis.title')}
        description={t('career.assessmentsettings.basis.description')}
        dirty={formikGeneralSettings.dirty}
        onSubmit={formikGeneralSettings.handleSubmit}
        isLoading={formikGeneralSettings.isSubmitting}
      >
        <div className='max-w-xl'>
          <Input
            id={'title'}
            label={t(TRANSLATION_LOCATION + 'edit_career_title_label')}
            type='text'
            placeholder={t(
              TRANSLATION_LOCATION + 'edit_career_title_placeholder'
            )}
            onChange={formikGeneralSettings.handleChange}
            onBlur={formikGeneralSettings.handleBlur}
            value={formikGeneralSettings.values.title}
            touched={formikGeneralSettings.touched.title}
            error={formikGeneralSettings.errors.title}
            disabled={formikGeneralSettings.isSubmitting}
          />
        </div>
        <div>
          <SelectMenu
            id='career_category'
            label={t(TRANSLATION_LOCATION + 'category_label')}
            options={getAllJobCategories()}
            defaultValue={formikGeneralSettings.values.category}
            onChange={(category) =>
              formikGeneralSettings.setFieldValue('category', category)
            }
            className='w-full sm:w-2/3'
          />
        </div>
        <div>
          <SelectMenu
            id='language'
            label={t(TRANSLATION_LOCATION + 'language_label')}
            options={languageOptions}
            lockedOptions={lockedLanguages}
            onLockedSelect={(language) => languageGates[language]?.open()}
            defaultValue={formikGeneralSettings.values.language}
            onChange={(language) =>
              formikGeneralSettings.setFieldValue('language', language)
            }
            description={getAssessmentLanguageDescription({
              t,
              language: formikGeneralSettings.values.language,
              savedLanguage,
              hasCustomMailTemplate
            })}
            className='w-full sm:w-2/3'
          />
          {keptLanguageGate && (
            <InfoCallout
              tone='neutral'
              icon={LockClosedIcon}
              className='mt-3 w-full sm:w-2/3'
              action={
                <LockBadge
                  feature={keptLanguageGate.feature}
                  source={LANGUAGE_SOURCE}
                  inUse
                />
              }
            >
              {t('pricing.gates.language_kept_hint', {
                language: t(getLanguage(savedLanguage))
              })}
            </InfoCallout>
          )}
        </div>
        <div>
          <UploadPicture
            title={t('career.assessmentsettings.basis.photo_url_title')}
            description={t(
              'components.settings.company_profile.input_partner_logo_hint'
            )}
            imageSrc={photoUrl}
            onSubmit={uploadPhoto}
            ImageCropComponent={ImageCropAssessment}
          />
        </div>
      </CareerSettingsContainer>
      <Divider />
      <ExpiresAfterDaysSettings career={career} updateHandler={updateHandler} />
      <Divider />
      <CareerSettingsContainer
        title={t('career.assessmentsettings.mail_template.title')}
        description={t('career.assessmentsettings.mail_template.description')}
        background='bg-white'
        padding='px-2'
        badge='MAIL_TEMPLATES'
      >
        <div className='-mt-4 divide-y divide-gray-100'>
          <MailTemplatePreviewCard
            template={{
              ...mailTemplate,
              badgeColor: 'green',
              subtitle: t(
                'career.assessmentsettings.mail_template.template_subtitle'
              )
            }}
            actions={
              <Button.PrimarySM
                text={t('career.assessmentsettings.mail_template.edit_action')}
                onClick={() => setShowEditMailTemplate(true)}
              />
            }
          />
          <MailTemplatePreviewCard
            template={{
              ...mailTemplateReminder,
              badgeColor: 'yellow',
              subtitle: t(
                'career.assessmentsettings.mail_template.reminder_template_subtitle'
              )
            }}
            actions={
              <Button.PrimarySM
                text={t('career.assessmentsettings.mail_template.edit_action')}
                onClick={() => setShowEditMailTemplateReminder(true)}
              />
            }
          />
          <MailTemplatePreviewCard
            template={{
              ...mailTemplateReject,
              badgeColor: 'red',
              subtitle: t(
                'career.assessmentsettings.mail_template.reject_template_subtitle'
              )
            }}
            actions={
              <Button.PrimarySM
                text={t('career.assessmentsettings.mail_template.edit_action')}
                onClick={() => setShowEditMailTemplateReject(true)}
              />
            }
          />
        </div>
      </CareerSettingsContainer>
      <Divider />
      {redirectGate.enforced ? (
        <CareerSettingsContainer
          title={t('career.assessmentsettings.redirect_url.title')}
          description={t('career.assessmentsettings.redirect_url.description')}
          dirty={formikRedirectUrl.dirty}
          onSubmit={redirectGate.locked ? null : formikRedirectUrl.handleSubmit}
          isLoading={formikRedirectUrl.isSubmitting}
          withLockedIcon={
            redirectGate.locked
              ? {
                  feature: REDIRECT_FEATURE,
                  source: REDIRECT_SOURCE,
                  inUse: redirectInUse
                }
              : null
          }
        >
          {/* In Nutzung bleibt die URL sichtbar und aktiv. Ändern ist
              gesperrt, Entfernen geht immer. */}
          <LockedSection
            feature={REDIRECT_FEATURE}
            source={REDIRECT_SOURCE}
            inUse={redirectInUse}
            allowedAction={
              redirectGate.readOnly ? (
                <Button.SecondaryBase
                  text={t('pricing.gates.redirect_remove_action')}
                  onClick={removeRedirectUrl}
                  isLoading={formikRedirectUrl.isSubmitting}
                />
              ) : null
            }
          >
            <div className='relative max-w-3xl'>{redirectUrlInput}</div>
          </LockedSection>
        </CareerSettingsContainer>
      ) : (
        <CareerSettingsContainer
          title={t('career.assessmentsettings.redirect_url.title')}
          description={t('career.assessmentsettings.redirect_url.description')}
          dirty={formikRedirectUrl.dirty}
          onSubmit={formikRedirectUrl.handleSubmit}
          isLoading={formikRedirectUrl.isSubmitting}
          withLockedIcon={
            redirectTalentUrl ? null : { onClick: () => showPaywall('general') }
          }
        >
          <div
            className={classNames(
              'relative',
              !redirectTalentUrl && 'cursor-pointer'
            )}
            onClick={() => {
              if (!redirectTalentUrl) {
                showPaywall('general')
              }
            }}
          >
            <div
              className={classNames(
                'relative max-w-3xl',
                !redirectTalentUrl && 'pointer-events-none blur-sm'
              )}
            >
              {redirectUrlInput}
            </div>
          </div>
        </CareerSettingsContainer>
      )}
      {!career.archived && (
        <>
          <Divider />
          <CareerSettingsContainer
            title={t('career.assessmentsettings.archive.title')}
            description={t(TRANSLATION_LOCATION + 'archive_description')}
          >
            <div>
              <Button.PrimaryLG
                text={t(TRANSLATION_LOCATION + 'archive_action')}
                onClick={archiveCareer}
                className='bg-red-600 hover:bg-red-500'
                isLoading={isArchiving}
              />
              <div className='mt-6 max-w-lg text-sm text-gray-700'>
                <p>{t(TRANSLATION_LOCATION + 'modal_archive_attention_1')}</p>
              </div>
              <div className='mt-2 text-sm leading-6'>
                <button
                  onClick={() => navigate('/recover/careers')}
                  className='font-semibold text-blue-600 hover:text-blue-500'
                >
                  {t('recover_careers.recover_action_text')}
                  <span aria-hidden='true'> &rarr;</span>
                </button>
              </div>
            </div>
          </CareerSettingsContainer>
        </>
      )}
      <EditMailTemplate
        open={showEditMailTemplate}
        setOpen={setShowEditMailTemplate}
        mailTemplate={mailTemplate}
        mailTemplateMacros={mailTemplateMacros}
        handleSubmit={({ result, onSuccess, onError }) => {
          const app_settings = career.app_settings
          app_settings.mail_template = result

          const input = { id: career.id, app_settings }
          updateHandler({ input, onSuccess, onError })
        }}
      />
      <EditMailTemplate
        open={showEditMailTemplateReminder}
        setOpen={setShowEditMailTemplateReminder}
        mailTemplate={mailTemplateReminder}
        mailTemplateMacros={mailTemplateMacrosReminder}
        handleSubmit={({ result, onSuccess, onError }) => {
          const app_settings = career.app_settings
          app_settings.mail_template_reminder = result

          const input = { id: career.id, app_settings }
          updateHandler({ input, onSuccess, onError })
        }}
      />
      <EditMailTemplate
        open={showEditMailTemplateReject}
        setOpen={setShowEditMailTemplateReject}
        mailTemplate={mailTemplateReject}
        mailTemplateMacros={mailTemplateMacrosReject}
        handleSubmit={({ result, onSuccess, onError }) => {
          const app_settings = career.app_settings
          app_settings.mail_template_reject = result

          const input = { id: career.id, app_settings }
          updateHandler({ input, onSuccess, onError })
        }}
      />
    </div>
  )
}

AssessmentSettings.propTypes = {
  career: PropTypes.object.isRequired,
  updateHandler: PropTypes.func.isRequired
}

export default AssessmentSettings
